/** The `?tab=` value that opens a document's Referrals tab, whatever its index. */
export const REFERRALS_TAB_PARAM: DocumentTabName = 'referrals';

/**
 * A document's Referrals tab. The path holds only the opaque document id and the query only
 * `tab`, which is on the analytics allow-list, so the link carries no PHI.
 */
export const getDocumentReferralTabUrl = (documentId: number): string =>
  `/documents/library/${documentId}?tab=${REFERRALS_TAB_PARAM}`;

export const getDocumentsLibraryUrlWithFilters = (currentSearch: string): string => {
  if (!currentSearch) {
    return '/documents/library';
  }

  // Parse the current search params
  const params = new URLSearchParams(currentSearch);

  // Remove the 'tab' parameter as it's specific to the document details view
  params.delete('tab');

  // If there are remaining params, append them to the URL
  const remainingParams = params.toString();
  if (remainingParams) {
    return `/documents/library?${remainingParams}`;
  }

  return '/documents/library';
};

/** The document detail's business tabs, addressable by name in `?tab=`. */
export const DOCUMENT_TAB_NAMES = {
  referrals: 'Referrals',
  'prior-authorizations': 'Prior Authorizations',
} as const;

/** A `?tab=` name the document detail resolves. A typo here no longer compiles. */
export type DocumentTabName = keyof typeof DOCUMENT_TAB_NAMES;

/**
 * The document detail's URL, opened on `tab` when one is given, carrying the list's filters.
 *
 * `search` is the list's own query string (with or without its leading `?`). Its filters are
 * kept so the detail's back link returns to the same filtered list, but any `tab` it carries is
 * dropped: that one belongs to a detail page, not to the list, and would otherwise override (or,
 * with no `tab` asked for, stand in for) the tab this link means to open.
 */
export const buildDocumentDetailUrl = (
  documentId: number,
  tab: DocumentTabName | undefined,
  search: string,
): string => {
  const params = new URLSearchParams();
  if (tab) {
    params.set('tab', tab);
  }
  new URLSearchParams(search).forEach((value, key) => {
    if (key !== 'tab') {
      params.append(key, value);
    }
  });
  const query = params.toString();
  return `/documents/library/${documentId}${query ? `?${query}` : ''}`;
};

/**
 * Which tab to show for a `?tab=` value, given the labels this document's tabs have.
 *
 * A business tab's index is not fixed: the detail appends one per detected document type, in
 * detection order, so a package with a prior-auth ahead of its referral puts Referrals at 3,
 * not 2. A name (`referrals`) is resolved against the labels instead. A name this document has
 * no tab for, an index out of range, or anything unparseable falls back to the first tab
 * rather than a tab strip with no panel under it.
 */
export const resolveDocumentTab = (requested: string | null, labels: string[]): number => {
  if (!requested) {
    return 0;
  }

  // Own keys only: a plain lookup also matched inherited ones (`?tab=constructor`).
  const label = Object.prototype.hasOwnProperty.call(DOCUMENT_TAB_NAMES, requested)
    ? DOCUMENT_TAB_NAMES[requested as DocumentTabName]
    : undefined;
  if (label) {
    const index = labels.indexOf(label);
    return index >= 0 ? index : 0;
  }

  const index = Number(requested);
  return Number.isInteger(index) && index >= 0 && index < labels.length ? index : 0;
};
